import { useMutation } from '@tanstack/react-query';
import type { PaymentIntent, Stripe, StripeError } from '@stripe/stripe-js';

export interface StripeConfirmSavedCardPaymentParams {
	/** Client secret from `beginPayment`. */
	clientSecret: string;
}

/**
 * Confirms a Stripe PaymentIntent that was begun against a card already on file,
 * via `stripe.confirmCardPayment`.
 *
 * The sibling of `useStripeConfirmPayment`, for the intents no Payment Element
 * collected. `beginPayment` pins such an intent to the stored method, so the
 * method is already attached and the confirmation needs no card details - only
 * the client secret, and a customer present to answer a 3DS challenge should the
 * card demand one.
 *
 * Note - this wraps a method from Stripe's SDK. This is not a method from the LW
 * core api
 */
const useStripeConfirmSavedCardPayment = (stripe: Stripe | null) => {
	return useMutation<
		PaymentIntent,
		StripeError | Error,
		StripeConfirmSavedCardPaymentParams
	>({
		mutationFn: async ({ clientSecret }) => {
			if (!stripe) {
				throw new Error('stripe is not defined');
			}

			const result = await stripe.confirmCardPayment(clientSecret);

			if (result.error) {
				throw result.error;
			}

			return result.paymentIntent;
		},
	});
};

export default useStripeConfirmSavedCardPayment;
